import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { LucideIcon } from "lucide-react";
import { Info } from "lucide-react";

export interface ResultMetric {
  label: string;
  value: string;
  highlight?: boolean;
  /** Optional detail shown below the value (e.g. "That's about X in words") */
  detail?: string;
  /** Optional tooltip explaining the metric (e.g. "CAGR = Compound Annual Growth Rate") */
  tooltip?: string;
}

interface ResultCardProps {
  title?: string;
  /** Optional icon next to title */
  icon?: LucideIcon;
  metrics: ResultMetric[];
  className?: string;
}

export function ResultCard({ title = "Results", icon: Icon, metrics, className }: ResultCardProps) {
  return (
    <Card className={cn("", className)}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {Icon && (
            <span className="rounded-lg bg-primary/10 p-1.5">
              <Icon className="size-4 text-primary" aria-hidden />
            </span>
          )}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {metrics.map((metric) => (
          <div
            key={metric.label}
            className="flex flex-col gap-1"
          >
            <div className="flex items-center justify-between gap-4">
              <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                {metric.label}
                {metric.tooltip && (
                  <Tooltip>
                    <TooltipTrigger
                      render={
                        <button
                          type="button"
                          className="inline-flex cursor-help rounded p-0.5 text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          aria-label={`More info: ${metric.tooltip}`}
                        >
                          <Info className="size-3.5" />
                        </button>
                      }
                    />
                    <TooltipContent side="top" className="max-w-[260px]">
                      {metric.tooltip}
                    </TooltipContent>
                  </Tooltip>
                )}
              </span>
              <span
                className={cn(
                  "text-right font-semibold tabular-nums",
                  metric.highlight && "text-lg text-primary"
                )}
              >
                {metric.value}
              </span>
            </div>
            {metric.detail && (
              <p className="mt-1 text-sm text-muted-foreground">
                <span className="font-medium not-italic text-foreground">
                  In words:
                </span>{" "}
                {metric.detail} dollars
              </p>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
